import React, { createContext, useState, useContext, useEffect } from 'react';
import {
    getAuth,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    GoogleAuthProvider,
    signInWithPopup,
    updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
// Remove uploadFileToFirebase as it's not needed for base64 implementation
// import { uploadFileToFirebase } from '../utils/firebaseUploadUtils';

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const auth = getAuth();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (authUser) => { // Renamed param to authUser
            try {
                if (authUser) {
                    const userDocumentRef = doc(db, 'users', authUser.uid); // Use specific name for clarity
                    const userDocSnap = await getDoc(userDocumentRef); // Use specific name for clarity
                    const firestoreData = userDocSnap.data();

                    if (firestoreData) {
                        // Firestore document exists. Merge auth data with Firestore data.
                        // Firestore's displayName and photoURL (base64) take precedence.
                        setUser({
                            uid: authUser.uid,
                            email: authUser.email, // email from auth
                            ...firestoreData, // Spread Firestore data first
                            displayName: firestoreData.displayName || authUser.displayName, // Explicitly Firestore first for displayName
                            photoURL: firestoreData.photoURL || authUser.photoURL, // Explicitly Firestore first for photoURL (base64)
                        });
                    } else {
                        // No Firestore document, or it's empty. Use data from Auth profile.
                        setUser({
                            uid: authUser.uid,
                            email: authUser.email,
                            displayName: authUser.displayName,
                            photoURL: authUser.photoURL,
                        });
                        if (!userDocSnap.exists() && authUser.email) {
                            console.warn(`User document for ${authUser.uid} not found in Firestore. Consider creating it if this is unexpected.`);
                        }
                    }
                } else {
                    // authUser is null (user signed out)
                    setUser(null);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                // Fallback: Try to set user from auth.currentUser if an error occurs during Firestore fetch
                // This part is tricky because authUser might be null if the error is unrelated to Firestore
                const fallbackAuthUser = auth.currentUser;
                if (fallbackAuthUser) {
                    setUser({
                        uid: fallbackAuthUser.uid,
                        email: fallbackAuthUser.email,
                        displayName: fallbackAuthUser.displayName,
                        photoURL: fallbackAuthUser.photoURL,
                    });
                } else {
                    setUser(null);
                }
            } finally {
                setLoading(false);
            }
        });

        return unsubscribe;
    }, [auth]);

    const signup = async (email, password, displayName) => {
        try {
            const { user } = await createUserWithEmailAndPassword(auth, email, password);

            // Update user profile
            await updateProfile(user, { displayName });

            // Create user document in Firestore
            await setDoc(doc(db, 'users', user.uid), {
                email,
                displayName,
                createdAt: new Date().toISOString(),
                projects: []
            });

            return user;
        } catch (error) {
            throw error;
        }
    };

    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const loginWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        try {
            const { user } = await signInWithPopup(auth, provider);

            // Check if user document exists
            const userDoc = await getDoc(doc(db, 'users', user.uid));

            if (!userDoc.exists()) {
                // Create user document if it doesn't exist
                await setDoc(doc(db, 'users', user.uid), {
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                    createdAt: new Date().toISOString(),
                    projects: []
                });
            }

            return user;
        } catch (error) {
            throw error;
        }
    };

    const logout = () => {
        return signOut(auth);
    };

    const updateUserProfile = async ({ displayName, photoFile }) => {
        if (!auth.currentUser) {
            throw new Error("No user is currently signed in.");
        }

        const currentUser = auth.currentUser;

        const authUpdates = {};
        const firestoreUpdates = {};

        // Handle display name update
        if (displayName && displayName !== user.displayName) {
            authUpdates.displayName = displayName;
            firestoreUpdates.displayName = displayName;
        }

        // Handle photo file to base64 conversion and storage
        if (photoFile) {
            try {
                const base64Photo = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(photoFile);
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = error => reject(error);
                });

                // It's generally too large for authUpdates.photoURL if it's a large image.
                // Firebase Auth photoURL is meant for URLs. Storing base64 here might work for small images
                // but can hit limits or be inefficient. Best practice is to store in Firestore/Storage
                // and use a URL if storing in Auth. Given the requirement, we'll store it in Firestore.
                // Store base64 string ONLY in Firestore.
                firestoreUpdates.photoURL = base64Photo;

                // DO NOT set authUpdates.photoURL with base64 string to avoid "auth/invalid-profile-attribute" (URL too long).
                // If there's a desire to clear a previously set photoURL from Firebase Auth (if it was a real URL from e.g. Google Sign-in),
                // we could set authUpdates.photoURL = null; or authUpdates.photoURL = '';
                // For now, we only update displayName in Auth if it changed, and let Firestore be the source of truth for the profile pic.
                // If displayName is the only thing in authUpdates, and it hasn't changed, this block might not run.
                // This is fine, as we only want to update Auth if displayName actually changed.
                // The photoURL in Auth will remain whatever it was (e.g., from Google, or null).
                // Our app logic will always prioritize Firestore for photoURL.

            } catch (error) {
                console.error("Error converting photo to base64:", error);
                throw error;
            }
        }

        // Determine if there are any actual updates for Auth profile (only displayName)
        const authProfileChanges = {};
        if (authUpdates.displayName) {
            authProfileChanges.displayName = authUpdates.displayName;
        }
        // Do not add photoURL to authProfileChanges

        if (Object.keys(authProfileChanges).length === 0 && Object.keys(firestoreUpdates).length === 0) {
            return { ...user }; // No changes to submit
        }

        // Update Firebase Auth profile (only displayName if it changed)
        if (Object.keys(authProfileChanges).length > 0) {
            try {
                await updateProfile(currentUser, authProfileChanges);
            } catch (error) {
                console.error("Error updating Firebase Auth profile (displayName):", error);
                throw error; // Propagate error
            }
        }

        // Update Firestore user document (displayName and/or photoURL)
        if (Object.keys(firestoreUpdates).length > 0) {
            const userDocRef = doc(db, 'users', currentUser.uid);
            try {
                await updateDoc(userDocRef, firestoreUpdates);
            } catch (error) {
                console.error("Error updating Firestore user document:", error);
                throw error;
            }
        }

        // Update local user state
        const finalUserUpdates = {};
        if (firestoreUpdates.displayName) finalUserUpdates.displayName = firestoreUpdates.displayName;
        if (firestoreUpdates.photoURL) finalUserUpdates.photoURL = firestoreUpdates.photoURL;

        setUser(prevUser => ({
            ...prevUser,
            ...finalUserUpdates,
        }));

        return { ...user, ...finalUserUpdates };
    };

    const value = {
        user,
        signup,
        login,
        loginWithGoogle,
        logout,
        updateUserProfile // Add the new function to the context value
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}